import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, {useState} from "react";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {styled} from "@mui/material/styles";
import {tableCellClasses} from "@mui/material/TableCell";
import {useDescriptionAlert} from "../../../../share";
import Box from "@mui/material/Box";
import {useDispatch} from "react-redux";
import {useGetGroupQuery as useGetCordwoodGroupQuery} from "../../../apis/Cordwood.api.ts";
import {useGetGroupQuery as useGetLogGroupQuery} from "../../../apis/Log.api.ts";
import {createWoodGroupsList, fetchWoodData, WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import GenericRootReportPdfDialog from "./GenericRootReportPdfDialog.tsx";
import {Trans} from "@lingui/react/macro";
import {t} from "@lingui/core/macro";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";

const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontSize: 14,
    },
}));

const StyledTableTypeClass = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        borderBottom: 'none',
    },
}));

const StyledTableStorage = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        fontWeight: 'bold',
        borderBottom: 'none',
        fontSize: '1em',
    },
}));

const StyledTableStorageTotal = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontWeight: 'bold',
    },
}));

const StyledTableFooter = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontWeight: 'bold',
        fontSize: '1em',
    },
}));

const GenericRootTable = ({title, toolbar, filter}) => {

    const dispatch = useDispatch()
    const {openFailAlert} = useDescriptionAlert()

    const [groupedData, setGroupedData] = useState<WoodGroupsList>(createWoodGroupsList())

    const logData = useGetLogGroupQuery(filter, {skip: filter.shipmentType == 'Cordwood'})
    const cordwoodData = useGetCordwoodGroupQuery(filter, {skip: filter.shipmentType == 'Logs'})

    React.useEffect(() => {
        let isSubscribed = true;

        if (!(logData.isFetching || cordwoodData.isFetching)) {
            fetchWoodData(
                dispatch,
                (filter.shipmentType == 'Cordwood' || !logData.data) ? [] : logData.data,
                (filter.shipmentType == 'Logs' || !cordwoodData.data) ? [] : cordwoodData.data,
            )
                .then(result => {
                    if (isSubscribed) {
                        result.isLoading = false
                        setGroupedData(result)
                    }
                })
                .catch(console.error)
        }

        if (logData.isError || cordwoodData.isError) {
            openFailAlert(t`Failed to load data`)
        }

        return () => isSubscribed = false;
    }, [filter.shipmentType, logData, cordwoodData,]);

    if (!groupedData.items || groupedData.isLoading || logData.isLoading || logData.isFetching || logData.isError || cordwoodData.isLoading || cordwoodData.isFetching || cordwoodData.isError) {
        return (
            <Paper
                sx={{padding: "20px", justifyContent: "center", alignItems: "center", display: "flex"}}>
                <GradientCircularProgress/>
            </Paper>
        )
    }

    return (
        <TableContainer>
            {groupedData && groupedData.items && !groupedData.isLoading &&
                <Box textAlign="right" sx={{flex: '0 0 auto'}}>
                    <GenericRootReportPdfDialog
                        title={title}
                        groupedData={groupedData}
                        filter={filter}
                        copies={1}
                    />
                    {toolbar && toolbar(groupedData)}
                </Box>
            }
            <Table stickyHeader aria-label="sticky table" size="small">
                <TableHead key="head">
                    <TableRow key="header">
                        <StyledTableCell key="storage"><Trans>Storage</Trans></StyledTableCell>
                        <StyledTableCell key="type"><Trans>Type</Trans></StyledTableCell>
                        <StyledTableCell key="class"><Trans>Class</Trans></StyledTableCell>
                        { filter.shipmentType != 'Cordwood' &&
                            <StyledTableCell key="thickness-class"><Trans>Thickness Class</Trans></StyledTableCell>
                        }
                        { filter.shipmentType != 'Cordwood' &&
                            <StyledTableCell key="count" align="right"><Trans>Logs Count</Trans></StyledTableCell>
                        }
                        <StyledTableCell key="volume" align="right"><Trans>Volume (m&#xB3;)</Trans></StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody key="body">
                    {groupedData.items
                        .sort((a, b) => a.cutting.name.localeCompare(b.cutting.name))
                        .map((cuttingRow) =>
                            <React.Fragment key={'fragment' + cuttingRow.cutting.id}>
                                {cuttingRow.items
                                    .sort((a, b) => a.storage.name.localeCompare(b.storage.name))
                                    .map((storageRow) =>
                                        <React.Fragment key={'fragment' + storageRow.storage.id}>
                                            <TableRow
                                                key={storageRow.storage.id}>
                                                <StyledTableStorage key="storage-id" colSpan={5}>
                                                    <Typography component="span" sx={{ fontSize: '1.1em' }}>{storageRow.storage.name} </Typography>
                                                    <Typography component="span" sx={{ fontSize: '0.8em' }}>({cuttingRow.cutting.name})</Typography>
                                                </StyledTableStorage>
                                            </TableRow>
                                            {storageRow.items
                                                .filter(a => !!(a.items))
                                                .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                .map((groupedRow, groupedRowIndex) =>
                                                    <React.Fragment
                                                        key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                        {groupedRow.items && groupedRow.items
                                                            .sort((a, b) => (a.thicknessClass.minDiameter || 0) - (b.thicknessClass.minDiameter || 0))
                                                            .map((itemRow, itemRowIndex) =>
                                                                itemRowIndex === 0 && groupedRowIndex === 0 ?
                                                                    <TableRow
                                                                        key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                        <StyledTableStorage key="storage-id"></StyledTableStorage>
                                                                        <StyledTableTypeClass
                                                                            key="type-id">{groupedRow.timberType.name}</StyledTableTypeClass>
                                                                        <StyledTableTypeClass
                                                                            key="class-id">{groupedRow.timberClass.name}</StyledTableTypeClass>
                                                                        { filter.shipmentType != 'Cordwood' &&
                                                                            <TableCell key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                        }
                                                                        { filter.shipmentType != 'Cordwood' &&
                                                                            <TableCell key="count" align="right">{itemRow.count}</TableCell>
                                                                        }
                                                                        <TableCell key="volume"
                                                                                   align="right">{fixedFormat(itemRow.sum)}</TableCell>
                                                                    </TableRow>
                                                                    :
                                                                    itemRowIndex === 0 ?
                                                                        <TableRow
                                                                            key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                            <StyledTableStorage key="storage-id"></StyledTableStorage>
                                                                            <StyledTableTypeClass
                                                                                key="type-id">{groupedRow.timberType.name}</StyledTableTypeClass>
                                                                            <StyledTableTypeClass
                                                                                key="class-id">{groupedRow.timberClass.name}</StyledTableTypeClass>
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <TableCell key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                            }
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <TableCell key="count" align="right">{itemRow.count}</TableCell>
                                                                            }
                                                                            <TableCell key="volume"
                                                                                       align="right">{fixedFormat(itemRow.sum)}</TableCell>
                                                                        </TableRow>
                                                                        :
                                                                        <TableRow
                                                                            key={storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                            <StyledTableStorage
                                                                                key="storage-id"></StyledTableStorage>
                                                                            <StyledTableTypeClass
                                                                                key="type-id"></StyledTableTypeClass>
                                                                            <StyledTableTypeClass
                                                                                key="class-id"></StyledTableTypeClass>
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <TableCell key="thickness-class-id">{itemRow.thicknessClass.name}</TableCell>
                                                                            }
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <TableCell key="count" align="right">{itemRow.count}</TableCell>
                                                                            }
                                                                            <TableCell key="volume"
                                                                                       align="right">{fixedFormat(itemRow.sum)}</TableCell>
                                                                        </TableRow>
                                                            )}
                                                        <TableRow
                                                            key={'total' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                            <StyledTableStorage
                                                                key="empty"></StyledTableStorage>
                                                            <StyledTableCell key="total" colSpan={ filter.shipmentType != 'Cordwood' ? 3 : 2}>
                                                                <Trans>Total class and
                                                                    type</Trans> ({groupedRow.timberType.name}, {groupedRow.timberClass.name})
                                                            </StyledTableCell>
                                                            { filter.shipmentType != 'Cordwood' &&
                                                                <StyledTableCell key="count" align="right">{groupedRow.count}</StyledTableCell>
                                                            }
                                                            <StyledTableCell key="sum"
                                                                             align="right">{fixedFormat(groupedRow.sum)}</StyledTableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                )}
                                            {storageRow.items
                                                .filter(a => !(a.items))
                                                .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                .map((groupedRow, groupedRowIndex) =>
                                                    <React.Fragment
                                                        key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                        <TableRow
                                                            key={'total' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                            <StyledTableStorage
                                                                key="empty"></StyledTableStorage>
                                                            <StyledTableCell key="total" colSpan={ filter.shipmentType != 'Cordwood' ? 3 : 2}>
                                                                <Trans>Total class and
                                                                    type</Trans> ({groupedRow.timberType.name}, {groupedRow.timberClass.name})
                                                            </StyledTableCell>
                                                            { filter.shipmentType != 'Cordwood' &&
                                                                <StyledTableCell key="count" align="right">{groupedRow.count}</StyledTableCell>
                                                            }
                                                            <StyledTableCell key="sum"
                                                                             align="right">{fixedFormat(groupedRow.sum)}</StyledTableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                )}
                                            <TableRow key={'total' + storageRow.storage.id}>
                                                <StyledTableStorageTotal key="total" colSpan={ filter.shipmentType != 'Cordwood' ? 4 : 3}>
                                                    <Trans>Total on storage</Trans>
                                                    <Typography component="span" sx={{ fontSize: '1.1em' }}> {storageRow.storage.name} </Typography>
                                                    <Typography component="span" sx={{ fontSize: '0.8em' }}>({cuttingRow.cutting.name})</Typography>
                                                </StyledTableStorageTotal>
                                                { filter.shipmentType != 'Cordwood' &&
                                                    <StyledTableStorageTotal key="count" align="right">{storageRow.count}</StyledTableStorageTotal>
                                                }
                                                <StyledTableStorageTotal key="sum"
                                                                         align="right">{fixedFormat(storageRow.sum)}</StyledTableStorageTotal>
                                            </TableRow>
                                        </React.Fragment>
                                    )
                                }
                                <TableRow key={'total' + cuttingRow.cutting.id}>
                                    <StyledTableStorageTotal key="total" colSpan={ filter.shipmentType != 'Cordwood' ? 4 : 3}>
                                        <Trans>Total on cutting</Trans>
                                        <Typography component="span" sx={{ fontSize: '1.1em' }}> {cuttingRow.cutting.name} </Typography>
                                    </StyledTableStorageTotal>
                                    { filter.shipmentType != 'Cordwood' &&
                                        <StyledTableStorageTotal key="count" align="right">{cuttingRow.count}</StyledTableStorageTotal>
                                    }
                                    <StyledTableStorageTotal key="sum"
                                                             align="right">{fixedFormat(cuttingRow.sum)}</StyledTableStorageTotal>
                                </TableRow>
                            </React.Fragment>
                        )
                    }
                    <TableRow key="Total">
                        <StyledTableFooter key="total" align="right"
                                           colSpan={ filter.shipmentType != 'Cordwood' ? 4 : 3}><Trans>Total</Trans></StyledTableFooter>
                        { filter.shipmentType != 'Cordwood' &&
                            <StyledTableFooter key="count" align="right">{groupedData.count}</StyledTableFooter>
                        }
                        <StyledTableFooter key="sum"
                                           align="right">{fixedFormat(groupedData.sum)}</StyledTableFooter>
                    </TableRow>
                </TableBody>
            </Table>
    </TableContainer>
    )
}

export default GenericRootTable;